import type { Identity } from "./identity";
import {
  isBreak,
  UNASSIGNED_DIVISION,
  type GameStats,
  type Player,
  type PointEvent,
  type ScheduledGame,
  type Team,
} from "./types";

/**
 * Tournament-wide player and team statistics, built from the per-game
 * `GameStats` snapshots.
 *
 * ## What this data can and cannot say
 *
 * Three properties of how the scorekeeper works decide the shape of everything
 * below, and every surface that renders these numbers has to repeat them:
 *
 * 1. **Credit is optional and unevenly collected.** The scorer/assist sheet is
 *    skippable by design (no modal may block scoring), so a volunteer who did
 *    not see the play records the point and nothing else. A player's totals
 *    therefore partly measure how diligent THEIR games' scorekeeper was, which
 *    is why `Coverage` is a first-class number here and not a footnote: an
 *    uncovered leaderboard hands the prize to whoever was luckiest with staff.
 * 2. **Defence is invisible.** Turnovers and blocks are deliberately untracked
 *    (see README): that would need a dedicated person per field. These are
 *    OFFENSIVE numbers and the UI must say so, or the top row reads as "best
 *    player at the tournament". Break goals and break assists are the closest
 *    honest proxy for D-line contribution: `points[]` records which team
 *    started each point on offence, so "scored while starting on defence" is
 *    real, recorded fact.
 * 3. **There is no playing time.** No lines, no attendance. Someone who played
 *    every point and someone who played three look identical apart from
 *    output, so `teamGames` is named for what it actually counts (games their
 *    TEAM played while they were on the roster), and never "games played".
 *
 * Consequently there is **no composite rating in this file**. MVP is a
 * judgment call; inventing a weighting (G + 0.8·A + a break bonus) would
 * manufacture an authority that gets argued with in front of an audience.
 * Rows are ranked by whichever column the reader chose, and ties are shown.
 *
 * ## What it reads
 *
 * Only `stats:<gameId>` snapshots, which is what makes post-game corrections
 * flow through for free (`applyCorrection` rewrites the snapshot), and what
 * makes a game reopened out of "final" stop counting (`clearResult` deletes
 * it, exactly as it deletes the recorded result). A game with no schedule row
 * has no snapshot at all: see `statsGaps` for how that is surfaced rather
 * than silently under-counted.
 */

// ---------- coverage ----------

/**
 * How much of a game's (or a team's, or a division's) scoring carries a
 * credit. Counts, not ratios, so they add up across games before dividing.
 */
export interface Coverage {
  /**
   * Points actually played. The STATED score, not the length of the point
   * list: `score_set` wipes point history on purpose, so a real 15-13 can
   * legitimately have no points at all. Using the list length as the
   * denominator would report that game as fully covered (0 of 0) when it is
   * the least covered game at the tournament.
   *
   * `max` of the two guards the other direction: a corrected snapshot may
   * state a score its point list disagrees with (`needsReconciliation` exists
   * precisely because the organiser is allowed to save that), and coverage
   * must never read above 100%.
   */
  scored: number;
  /** Points with a row in the list at all. */
  recorded: number;
  /** Rows naming a scorer. This is the number that matters. */
  credited: number;
  /**
   * Rows naming an assist. Deliberately reported separately and never as a
   * diligence measure: a Callahan has no assist, and nothing in the data
   * distinguishes "there was no assist" from "nobody wrote it down".
   */
  assisted: number;
  /**
   * Rows whose scorer resolves to a player in that game's frozen roster.
   * Below `credited` only when a team played with no roster entered (or an
   * id that predates one), which is a different problem from a lazy sheet:
   * the credit exists, it just cannot be attributed to a person.
   */
  attributed: number;
}

export function emptyCoverage(): Coverage {
  return { scored: 0, recorded: 0, credited: 0, assisted: 0, attributed: 0 };
}

export function addCoverage(a: Coverage, b: Coverage): Coverage {
  return {
    scored: a.scored + b.scored,
    recorded: a.recorded + b.recorded,
    credited: a.credited + b.credited,
    assisted: a.assisted + b.assisted,
    attributed: a.attributed + b.attributed,
  };
}

/** Share of played points that name a scorer, or null when nothing was played:
 *  "no games yet" and "0% credited" are different answers. */
export function creditRate(c: Coverage): number | null {
  return c.scored === 0 ? null : c.credited / c.scored;
}

/** Points that were played but carry no scorer. The chase-list number. */
export function uncredited(c: Coverage): number {
  return Math.max(0, c.scored - c.credited);
}

// ---------- per (player, game) rows: the seam everything folds from ----------

/**
 * One player's line in one game.
 *
 * Every aggregate below folds this list, which keeps a single place that knows
 * how a point becomes a credit, and leaves cross-team identity merging
 * (ROADMAP §2, cross-division MVP) into a matter of folding by a different key
 * rather than a second implementation of the same maths.
 */
export interface PlayerGameLine {
  playerId: string;
  gameId: string;
  /** The snapshot's own copy: a later roster edit never rewrites history. */
  frozenName: string;
  frozenNumber: string;
  /** Team as recorded in the snapshot; null for an ad-hoc typed-in side. */
  teamId: string | null;
  teamShort: string;
  teamName: string;
  side: "A" | "B";
  scoreFor: number;
  scoreAgainst: number;
  goals: number;
  assists: number;
  breakGoals: number;
  breakAssists: number;
  /**
   * Goals that ended the game, and the subset of those that ended a TIED game:
   * universe point in the words everyone uses. Derived without the game
   * format: the snapshot carries no `scoreTarget`, so "was GAME POINT on the
   * banner" is not knowable for the games already recorded, while "won it" is.
   */
  gameWinners: number;
  universeGoals: number;
  /** That side's coverage in that game: the same number for every player on
   *  the team, and the context a candidate's totals have to be read against. */
  coverage: Coverage;
}

interface SideStats {
  teamId: string | null;
  name: string;
  short: string;
  roster?: Player[];
}

function sideOf(gs: GameStats, side: "A" | "B"): SideStats {
  return side === "A" ? gs.teamA : gs.teamB;
}

/** Coverage of one side of one game: only the points that side scored. */
export function sideCoverage(gs: GameStats, side: "A" | "B"): Coverage {
  const stated = side === "A" ? gs.scoreA : gs.scoreB;
  const mine = gs.points.filter((p) => p.team === side);
  const roster = sideOf(gs, side).roster ?? [];
  return {
    scored: Math.max(stated, mine.length),
    recorded: mine.length,
    credited: mine.filter((p) => p.scorerId !== null).length,
    assisted: mine.filter((p) => p.assistId !== null).length,
    attributed: mine.filter((p) => p.scorerId !== null && roster.some((r) => r.id === p.scorerId))
      .length,
  };
}

/** Coverage of a whole game, both sides. */
export function gameCoverage(gs: GameStats): Coverage {
  return addCoverage(sideCoverage(gs, "A"), sideCoverage(gs, "B"));
}

/**
 * The winning goal of a game, if the point list is complete enough to know
 * which one it was.
 *
 * A truncated or absent list must not crown the last point that happens to be
 * written down, so this only answers when the recorded points add up to the
 * stated score. `tied` is read from the score BEFORE the point, which is what
 * universe point means: the deciding point of a level game.
 */
function decidingPoint(gs: GameStats): { point: PointEvent; tied: boolean } | null {
  const last = gs.points[gs.points.length - 1];
  if (last === undefined) return null;
  if (last.scoreA !== gs.scoreA || last.scoreB !== gs.scoreB) return null;
  const beforeA = last.team === "A" ? last.scoreA - 1 : last.scoreA;
  const beforeB = last.team === "B" ? last.scoreB - 1 : last.scoreB;
  // A draw decides nothing, and neither does a game abandoned level.
  if (gs.scoreA === gs.scoreB) return null;
  return { point: last, tied: beforeA === beforeB };
}

/**
 * Every (player, game) line in the tournament.
 *
 * A player appears for a game when they are on that side's FROZEN roster,
 * whether or not they scored: a squad member with no credit is a real row on
 * their own profile and on the team's, and only the leaderboard filters them
 * out. That also makes `teamGames` honest: a player added to the roster
 * halfway through the weekend is not charged with the mornings they missed.
 */
export function playerGameLines(stats: readonly GameStats[]): PlayerGameLine[] {
  const out: PlayerGameLine[] = [];

  for (const gs of stats) {
    const deciding = decidingPoint(gs);
    for (const side of ["A", "B"] as const) {
      const team = sideOf(gs, side);
      const roster = team.roster ?? [];
      const coverage = sideCoverage(gs, side);
      const scoreFor = side === "A" ? gs.scoreA : gs.scoreB;
      const scoreAgainst = side === "A" ? gs.scoreB : gs.scoreA;

      const lines = new Map<string, PlayerGameLine>();
      const line = (playerId: string): PlayerGameLine | undefined => {
        const known = lines.get(playerId);
        if (known !== undefined) return known;
        const player = roster.find((r) => r.id === playerId);
        // A credit naming somebody who is not on the frozen roster (a team
        // that played with no roster entered) counts in `coverage.credited`
        // but has no person to hang a row on. That gap is what `attributed`
        // reports, and it must not invent a player here.
        if (player === undefined) return undefined;
        const fresh: PlayerGameLine = {
          playerId,
          gameId: gs.scheduleGameId,
          frozenName: player.name,
          frozenNumber: player.number,
          teamId: team.teamId,
          teamShort: team.short,
          teamName: team.name,
          side,
          scoreFor,
          scoreAgainst,
          goals: 0,
          assists: 0,
          breakGoals: 0,
          breakAssists: 0,
          gameWinners: 0,
          universeGoals: 0,
          coverage,
        };
        lines.set(playerId, fresh);
        return fresh;
      };

      // Seed the roster first so a player who never scored still has a row.
      for (const player of roster) line(player.id);

      for (const point of gs.points) {
        if (point.team !== side) continue;
        const isB = isBreak(point);
        if (point.scorerId !== null) {
          const row = line(point.scorerId);
          if (row !== undefined) {
            row.goals += 1;
            if (isB) row.breakGoals += 1;
            if (deciding !== null && deciding.point === point) {
              row.gameWinners += 1;
              if (deciding.tied) row.universeGoals += 1;
            }
          }
        }
        if (point.assistId !== null) {
          const row = line(point.assistId);
          if (row !== undefined) {
            row.assists += 1;
            if (isB) row.breakAssists += 1;
          }
        }
      }

      out.push(...lines.values());
    }
  }
  return out;
}

// ---------- player totals ----------

export interface PlayerStatRow {
  /** A `Player.id`, or a `Person.id` when roster entries have been merged. */
  playerId: string;
  /** Latest name, resolved from the team's CURRENT roster by id, falling back
   *  to the newest snapshot. Identity is the id; the label follows the roster
   *  so a corrected spelling shows everywhere, while the snapshots that record
   *  who actually scored stay frozen. */
  name: string;
  number: string;
  teamId: string | null;
  teamShort: string;
  teamName: string;
  teamColor: string | null;
  /** `UNASSIGNED_DIVISION` when the team has no division, or none is known. */
  divisionId: string;
  goals: number;
  assists: number;
  /** Goals + assists. A COUNT of credited involvements, not a rating: the two
   *  are not interchangeable and this file does not claim they are. */
  total: number;
  breakGoals: number;
  breakAssists: number;
  gameWinners: number;
  universeGoals: number;
  /** Games their TEAM played while they were on the roster. Not attendance:
   *  see the file header. */
  teamGames: number;
  /** Every team short this row's games were played for, primary first. One
   *  entry unless merged roster entries brought a second. */
  teamShorts: string[];
  /** More than one roster entry folded into this row (an admin-confirmed
   *  merge). The tables say so, because a merged row is a claim about a human
   *  that somebody made on purpose. */
  merged: boolean;
  /** Coverage of exactly those games, their side only. */
  coverage: Coverage;
}

export interface StatsInput {
  stats: readonly GameStats[];
  teams: readonly Team[];
  /** Schedule rows supply the division tag and the start time; snapshots
   *  carry neither. A snapshot whose row is gone still counts: see
   *  `statsGaps`. */
  schedule: readonly ScheduledGame[];
}

/**
 * Division a recorded game belongs to. The schedule row's explicit tag wins;
 * otherwise both sides' teams must agree, which is how games entered before
 * divisions existed still land in the right bucket. Anything else is
 * `UNASSIGNED_DIVISION`: a bucket, never a crash.
 */
export function gameDivision(
  gs: GameStats,
  input: Pick<StatsInput, "teams" | "schedule">,
): string {
  const row = input.schedule.find((g) => g.id === gs.scheduleGameId);
  if (row?.divisionId !== undefined) return row.divisionId;
  const divisionOf = (teamId: string | null): string | undefined =>
    teamId === null ? undefined : input.teams.find((t) => t.id === teamId)?.divisionId;
  const a = divisionOf(gs.teamA.teamId);
  const b = divisionOf(gs.teamB.teamId);
  if (a !== undefined && a === b) return a;
  return UNASSIGNED_DIVISION;
}

/** A team's division, bucketed. */
export function teamDivision(team: Team | undefined): string {
  return team?.divisionId ?? UNASSIGNED_DIVISION;
}

/**
 * Every player who appears on a recorded roster, with their totals.
 *
 * Includes players with nothing credited (they belong on a roster view and on
 * their own profile); the leaderboard is the surface that filters them out.
 */
/**
 * Every player who appears on a recorded roster, with their totals, keyed
 * within their own DIVISION.
 *
 * Merged roster entries collapse only when they land in the SAME division:
 * a person playing open and mixed keeps one row per division, each counting
 * only that division's games, so confirming a merge can never move goals out
 * of one division's table and into another's. Two clubs' squads inside one
 * division do collapse, which is the whole point of the merge.
 *
 * Includes players with nothing credited (they belong on a roster view and on
 * their own profile); the leaderboard is the surface that filters them out.
 */
export function playerStats(input: StatsInput, identity?: Identity): PlayerStatRow[] {
  return foldLines(input, identity, false);
}

/**
 * One row per PERSON across every division: the combined table.
 *
 * Additive by construction: it is a second fold of the same lines, never a
 * transformation of the per-division ones. Its `divisionId` is the primary
 * team's and is meaningless as a filter; nothing may narrow this list by it.
 */
export function combinedStats(input: StatsInput, identity?: Identity): PlayerStatRow[] {
  return foldLines(input, identity, true);
}

interface Draft extends PlayerStatRow {
  /** Lines per member and per team, so the primary of each can be picked
   *  after the fold rather than guessed from whichever line arrived first. */
  memberCounts: Map<string, number>;
  teamCounts: Map<string, number>;
  /**
   * Games (and game-sides) already counted into this row. A merged person can
   * legitimately produce two LINES for one game — the duplicate-roster-entry
   * merge `identity.ts` recommends, or one human on both frozen rosters — and
   * folding per line double-counted `teamGames` (halving per-game rates,
   * prematurely clearing the minimum-games gate) and doubled the coverage
   * context printed beside an MVP candidate. Goals were always safe: they sum.
   */
  seenGames: Set<string>;
  seenGameSides: Set<string>;
}

function foldLines(
  input: StatsInput,
  identity: Identity | undefined,
  combined: boolean,
): PlayerStatRow[] {
  const lines = playerGameLines(input.stats);
  const byId = new Map<string, Team>(input.teams.map((t) => [t.id, t]));
  const drafts = new Map<string, Draft>();

  for (const line of lines) {
    const team = line.teamId === null ? undefined : byId.get(line.teamId);
    const division = teamDivision(team);
    const personId = identity?.idOf(line.playerId) ?? line.playerId;
    // Per-division rows are keyed by (person, division): see playerStats.
    const key = combined ? personId : `${personId}::${division}`;

    const row = drafts.get(key) ?? {
      playerId: personId,
      name: line.frozenName,
      number: line.frozenNumber,
      teamId: line.teamId,
      teamShort: line.teamShort,
      teamName: team?.name ?? line.teamName,
      teamColor: team?.color ?? null,
      divisionId: division,
      goals: 0,
      assists: 0,
      total: 0,
      breakGoals: 0,
      breakAssists: 0,
      gameWinners: 0,
      universeGoals: 0,
      teamGames: 0,
      teamShorts: [],
      merged: false,
      coverage: emptyCoverage(),
      memberCounts: new Map<string, number>(),
      teamCounts: new Map<string, number>(),
      seenGames: new Set<string>(),
      seenGameSides: new Set<string>(),
    };

    row.goals += line.goals;
    row.assists += line.assists;
    row.total = row.goals + row.assists;
    row.breakGoals += line.breakGoals;
    row.breakAssists += line.breakAssists;
    row.gameWinners += line.gameWinners;
    row.universeGoals += line.universeGoals;
    // DISTINCT games and game-sides, not lines: see `Draft.seenGames`.
    if (!row.seenGames.has(line.gameId)) {
      row.seenGames.add(line.gameId);
      row.teamGames += 1;
    }
    const gameSide = `${line.gameId}::${line.side}`;
    if (!row.seenGameSides.has(gameSide)) {
      row.seenGameSides.add(gameSide);
      row.coverage = addCoverage(row.coverage, line.coverage);
    }
    row.memberCounts.set(line.playerId, (row.memberCounts.get(line.playerId) ?? 0) + 1);
    if (line.teamId !== null) {
      row.teamCounts.set(line.teamId, (row.teamCounts.get(line.teamId) ?? 0) + 1);
    }
    if (!row.teamShorts.includes(line.teamShort)) row.teamShorts.push(line.teamShort);
    drafts.set(key, row);
  }

  return [...drafts.values()].map((draft) => {
    const { memberCounts, teamCounts, seenGames: _sg, seenGameSides: _ss, ...row } = draft;
    const primaryMember = mostPlayed(memberCounts);
    const primaryTeam = mostPlayed(teamCounts);
    const team = primaryTeam === null ? undefined : byId.get(primaryTeam);
    // The snapshot's copy is the fallback; the live roster is the truth about
    // how a name is spelled TODAY. A merged row wears the person's own name,
    // because two rosters routinely spell one person's name two ways.
    const current =
      primaryMember === null ? undefined : team?.roster?.find((p) => p.id === primaryMember);
    const merged = memberCounts.size > 1;
    const personName = identity?.nameOf(row.playerId);
    return {
      ...row,
      merged,
      name: (merged ? personName : undefined) ?? current?.name ?? row.name,
      number: current?.number ?? row.number,
      ...(team !== undefined
        ? {
            teamId: team.id,
            teamShort: team.short,
            teamName: team.name,
            teamColor: team.color,
            divisionId: combined ? teamDivision(team) : row.divisionId,
          }
        : {}),
      // Primary first, so a merged row reads "LYN + ODS" with the team they
      // played most for at the front.
      teamShorts: [
        ...(team !== undefined ? [team.short] : []),
        ...row.teamShorts.filter((short) => short !== team?.short),
      ],
    };
  });
}

/** The key with the most lines. Ties keep insertion order, which is the order
 *  the games were recorded in: stable, and nobody has to explain it. */
function mostPlayed(counts: Map<string, number>): string | null {
  let best: { id: string; n: number } | null = null;
  for (const [id, n] of counts) {
    if (best === null || n > best.n) best = { id, n };
  }
  return best?.id ?? null;
}

/** One player's games, most recent first when the schedule knows the time. */
export function playerGames(
  playerId: string,
  input: StatsInput,
  identity?: Identity,
): PlayerGameLine[] {
  const at = new Map(input.schedule.map((g) => [g.id, g.startAt]));
  return playerGameLines(input.stats)
    // `playerId` may be a person id, so compare through the same resolver the
    // tables folded with: otherwise a merged row opens an empty card.
    .filter((l) => (identity?.idOf(l.playerId) ?? l.playerId) === playerId)
    .sort((a, b) => (at.get(b.gameId) ?? "").localeCompare(at.get(a.gameId) ?? ""));
}

// ---------- ranking ----------

export const STAT_KEYS = ["goals", "assists", "total", "breaks"] as const;
export type StatKey = (typeof STAT_KEYS)[number];

export const STAT_LABEL: Record<StatKey, string> = {
  goals: "Goals",
  assists: "Assists",
  total: "Goals + assists",
  breaks: "Break involvements",
};

export function statValue(row: PlayerStatRow, key: StatKey): number {
  switch (key) {
    case "goals":
      return row.goals;
    case "assists":
      return row.assists;
    case "total":
      return row.total;
    case "breaks":
      return row.breakGoals + row.breakAssists;
  }
}

/** Per team-game. Null with no games: a rate off zero games is not zero. */
export function statRate(row: PlayerStatRow, key: StatKey): number | null {
  return row.teamGames === 0 ? null : statValue(row, key) / row.teamGames;
}

export type RankMode = "total" | "perGame";

export interface RankedPlayerRow extends PlayerStatRow {
  /** Null below the minimum-games threshold: shown, not ranked. */
  rank: number | null;
  ranked: boolean;
  /** Level with at least one other ranked row on the active column. */
  tied: boolean;
}

/**
 * Integer thousandths, for the same reason `spirit.ts` uses them: two rates of
 * 6/4 and 9/6 are the same number and must compare equal, and float equality
 * does not reliably agree.
 */
function key3(value: number): number {
  return Math.round(value * 1000);
}

/**
 * Rank by ONE chosen column, with ties shown as ties.
 *
 * There is no default column and no blended score on purpose: the reader
 * picks what they are comparing, and the table says which column it ranked by.
 * Dense ranking (1, 2, 2, 3), name order as the final
 * stabiliser and never a separator: it makes rendering deterministic without
 * claiming anybody won.
 */
export function rankPlayers(
  rows: readonly PlayerStatRow[],
  opts: { key: StatKey; mode?: RankMode; minGames: number },
): RankedPlayerRow[] {
  const mode = opts.mode ?? "total";
  const score = (row: PlayerStatRow): number =>
    mode === "perGame" ? key3(statRate(row, opts.key) ?? 0) : statValue(row, opts.key);

  const sorted: RankedPlayerRow[] = rows
    .map((r) => ({ ...r, rank: null as number | null, ranked: r.teamGames >= opts.minGames, tied: false }))
    .sort((a, b) => {
      if (a.ranked !== b.ranked) return a.ranked ? -1 : 1;
      return score(b) - score(a) || a.name.localeCompare(b.name);
    });

  let rank = 0;
  for (let i = 0; i < sorted.length; i += 1) {
    const row = sorted[i];
    if (row === undefined) continue;
    if (!row.ranked) {
      row.rank = null;
      row.tied = false;
      continue;
    }
    const prev = sorted[i - 1];
    const level = (x: RankedPlayerRow | undefined): boolean =>
      x !== undefined && x.ranked && score(x) === score(row);
    if (!level(prev)) rank += 1;
    row.rank = rank;
    row.tied = level(prev) || level(sorted[i + 1]);
  }
  return sorted;
}

/** Leaderboard rows: everyone with something credited. A player with no
 *  credit belongs on a roster view, not in a table of scorers. */
export function withCredit(rows: readonly PlayerStatRow[]): PlayerStatRow[] {
  return rows.filter((r) => r.total > 0);
}

/** Rows for one division bucket. */
export function inDivision(rows: readonly PlayerStatRow[], divisionId: string): PlayerStatRow[] {
  return rows.filter((r) => r.divisionId === divisionId);
}

// ---------- coverage roll-ups ----------

/** Coverage of every recorded game in the tournament. */
export function tournamentCoverage(input: Pick<StatsInput, "stats">): Coverage {
  return input.stats.reduce((acc, gs) => addCoverage(acc, gameCoverage(gs)), emptyCoverage());
}

/** Coverage of one division's recorded games, both sides of each. */
export function divisionCoverage(input: StatsInput, divisionId: string): Coverage {
  return input.stats
    .filter((gs) => gameDivision(gs, input) === divisionId)
    .reduce((acc, gs) => addCoverage(acc, gameCoverage(gs)), emptyCoverage());
}

/** Coverage of one team's own scoring. */
export function teamCoverage(input: Pick<StatsInput, "stats">, teamId: string): Coverage {
  let out = emptyCoverage();
  for (const gs of input.stats) {
    for (const side of ["A", "B"] as const) {
      if (sideOf(gs, side).teamId !== teamId) continue;
      out = addCoverage(out, sideCoverage(gs, side));
    }
  }
  return out;
}

/** Per-game coverage, worst first: the organiser's chase list. */
export interface GameCoverageRow {
  gameId: string;
  startAt: string | null;
  divisionId: string;
  label: string;
  coverage: Coverage;
}

export function gameCoverageRows(input: StatsInput): GameCoverageRow[] {
  return input.stats
    .map((gs) => {
      const row = input.schedule.find((g) => g.id === gs.scheduleGameId);
      const coverage = gameCoverage(gs);
      return {
        gameId: gs.scheduleGameId,
        startAt: row?.startAt ?? null,
        divisionId: gameDivision(gs, input),
        // The snapshot's own names: who actually played, whatever the slots
        // were later rewired to.
        label: `${gs.teamA.name} ${gs.scoreA}–${gs.scoreB} ${gs.teamB.name}`,
        coverage,
      };
    })
    .sort(
      (a, b) =>
        uncredited(b.coverage) - uncredited(a.coverage) ||
        (a.startAt ?? "").localeCompare(b.startAt ?? ""),
    );
}

// ---------- what these numbers are NOT built from ----------

export interface StatsGaps {
  /** Finished games with no snapshot: a result typed straight into the
   *  schedule, or recorded before stats existed. Their points are not here. */
  resultsWithoutSnapshot: ScheduledGame[];
  /** Snapshots whose schedule row has since gone. They still count (the game
   *  was played), but nothing can say when or in which division. */
  snapshotsWithoutSchedule: string[];
  /** Recorded games with a side that is a typed-in name rather than a team.
   *  Its players cannot reach a team profile or a division table. */
  adHocSides: string[];
  /** Teams that played with no roster entered: their scorers can never be
   *  credited to anybody, however diligent the scorekeeper was. */
  teamsWithoutRoster: string[];
}

/**
 * Everything the leaderboards cannot see, named rather than silently missing.
 *
 * The biggest hole has no entry here because it leaves no trace at all: a game
 * set up on the scorekeeper WITHOUT picking a schedule row writes no snapshot
 * (`syncResult` needs a `scheduleGameId`), so its points vanish at the next
 * setup. Nothing in storage remembers it happened: the admin view checks the
 * live field states for one in progress and says so while it can still be
 * fixed by scheduling the game.
 */
export function statsGaps(input: StatsInput): StatsGaps {
  const snapshots = new Set(input.stats.map((s) => s.scheduleGameId));
  const scheduled = new Set(input.schedule.map((g) => g.id));
  const adHocSides: string[] = [];
  const teamsWithoutRoster = new Set<string>();

  for (const gs of input.stats) {
    for (const side of ["A", "B"] as const) {
      const team = sideOf(gs, side);
      if (team.teamId === null) {
        if (!adHocSides.includes(gs.scheduleGameId)) adHocSides.push(gs.scheduleGameId);
        continue;
      }
      if ((team.roster ?? []).length === 0) teamsWithoutRoster.add(team.teamId);
    }
  }

  return {
    resultsWithoutSnapshot: input.schedule.filter(
      (g) => g.result !== undefined && !snapshots.has(g.id),
    ),
    snapshotsWithoutSchedule: input.stats
      .filter((s) => !scheduled.has(s.scheduleGameId))
      .map((s) => s.scheduleGameId),
    adHocSides,
    teamsWithoutRoster: [...teamsWithoutRoster],
  };
}

// ---------- team profiles ----------

export interface ScorerPair {
  scorerId: string;
  assistId: string;
  goals: number;
}

export interface TeamStatRow {
  team: Team;
  divisionId: string;
  games: number;
  pointsFor: number;
  pointsAgainst: number;
  /** Null with no recorded games: an average of nothing is not zero. */
  avgMargin: number | null;
  breaksFor: number;
  /** Breaks CONCEDED: points the opponent scored starting on defence. */
  breaksAgainst: number;
  /** Most points scored in a row within one game, and where. */
  longestRun: number;
  longestRunGameId: string | null;
  coverage: Coverage;
  /** Most frequent assist→goal combinations, best first. Ids, because the
   *  caller already has the name lookup and the pair is data, not a label. */
  pairings: ScorerPair[];
}

export function teamStats(input: StatsInput): TeamStatRow[] {
  const rows = new Map<string, TeamStatRow>();

  for (const gs of input.stats) {
    for (const side of ["A", "B"] as const) {
      const team = sideOf(gs, side);
      if (team.teamId === null) continue;
      const known = input.teams.find((t) => t.id === team.teamId);
      if (known === undefined) continue; // deleted team: nothing to profile

      const row = rows.get(known.id) ?? {
        team: known,
        divisionId: teamDivision(known),
        games: 0,
        pointsFor: 0,
        pointsAgainst: 0,
        avgMargin: null,
        breaksFor: 0,
        breaksAgainst: 0,
        longestRun: 0,
        longestRunGameId: null,
        coverage: emptyCoverage(),
        pairings: [],
      };

      row.games += 1;
      row.pointsFor += side === "A" ? gs.scoreA : gs.scoreB;
      row.pointsAgainst += side === "A" ? gs.scoreB : gs.scoreA;
      row.coverage = addCoverage(row.coverage, sideCoverage(gs, side));

      let run = 0;
      const pairs = new Map<string, ScorerPair>();
      for (const point of gs.points) {
        if (point.team === side) {
          run += 1;
          if (run > row.longestRun) {
            row.longestRun = run;
            row.longestRunGameId = gs.scheduleGameId;
          }
          if (isBreak(point)) row.breaksFor += 1;
          if (point.scorerId !== null && point.assistId !== null) {
            const key = `${point.assistId}→${point.scorerId}`;
            const pair = pairs.get(key) ?? {
              scorerId: point.scorerId,
              assistId: point.assistId,
              goals: 0,
            };
            pair.goals += 1;
            pairs.set(key, pair);
          }
        } else {
          run = 0;
          if (isBreak(point)) row.breaksAgainst += 1;
        }
      }
      for (const pair of pairs.values()) {
        const existing = row.pairings.find(
          (p) => p.scorerId === pair.scorerId && p.assistId === pair.assistId,
        );
        if (existing === undefined) row.pairings.push({ ...pair });
        else existing.goals += pair.goals;
      }

      rows.set(known.id, row);
    }
  }

  const out = [...rows.values()];
  for (const row of out) {
    row.avgMargin = row.games === 0 ? null : (row.pointsFor - row.pointsAgainst) / row.games;
    row.pairings.sort((a, b) => b.goals - a.goals || a.scorerId.localeCompare(b.scorerId));
  }
  return out.sort((a, b) => a.team.name.localeCompare(b.team.name));
}

// ---------- labels ----------

/** Which teams a recorded game was between, for a player's game list. Reads
 *  the SNAPSHOT, so a rewired bracket never rewrites who somebody played. */
export function opponentName(line: PlayerGameLine, stats: readonly GameStats[]): string {
  const gs = stats.find((s) => s.scheduleGameId === line.gameId);
  if (gs === undefined) return "—";
  return line.side === "A" ? gs.teamB.name : gs.teamA.name;
}
